import { memo, useCallback } from 'react'
import { XMarkIcon, MapPinIcon, CameraIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import { sightingAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'
import { formatRelativeTime } from '../../utils/dateFormatting'

const logger = createLogger('NearbyEntityPrompt')

/**
 * NearbyEntityPrompt
 *
 * Floating pill that appears when the user's GPS location is within ~150m
 * of a known entity. Offers a quick-confirm action inline.
 *
 * @param {Object}   props.entity          — the nearby entity
 * @param {Function} props.onDismiss       — called with entityId when dismissed
 * @param {boolean}  props.isImmersiveMode
 * @param {Function} props.onTabChange     — immersive tab switcher
 * @param {Function} props.navigate        — react-router navigate
 */
const NearbyEntityPrompt = memo(function NearbyEntityPrompt({
  entity,
  onDismiss,
  nearbyCount = 1,
  queueIndex = 1,
  isImmersiveMode = false,
  onTabChange,
  navigate,
}) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { loading: isStillHereLoading, execute: executeStillHere } = useAsyncOperation()

  const entityId = entity?.entityId || entity?.id
  const sightingId = entity?.id  // entity.id is the sighting ID on map entities
  const isOwnSighting = !!(user && entity?.currentSightingUserId && user.id === entity.currentSightingUserId)

  const handleStillHere = useCallback((e) => {
    e.stopPropagation()
    if (!user) {
      if (isImmersiveMode && onTabChange) onTabChange('auth')
      else if (navigate) navigate('/login', { state: { from: window.location.pathname } })
      return
    }
    const targetEntityId = entity?.entityId || entity?.entity_id
    const lat = entity?.currentLatitude
    const lng = entity?.currentLongitude
    if (!targetEntityId || !lat || !lng) return
    executeStillHere(
      () => sightingAPI.createSighting({ entityId: targetEntityId, latitude: lat, longitude: lng, sightingType: 'tag' }, { silentError: true }),
      {
        silent: true,
        onSuccess: () => {
          toast.success(t('sightings.stillHereSuccess') || 'Location updated! Thanks for keeping the map fresh.')
          onDismiss(sightingId)
        },
        onError: (err) => {
          logger.error('Still Here failed', err)
          toast.error(err?.response?.data?.error || t('toast.error.generic') || 'Something went wrong')
          onDismiss(sightingId)
        },
      }
    )
  }, [user, entity, sightingId, executeStillHere, onDismiss, isImmersiveMode, onTabChange, navigate, t])

  const handleConfirmWithPhoto = useCallback((e) => {
    e.stopPropagation()
    if (!user) {
      if (isImmersiveMode && onTabChange) {
        onTabChange('auth')
      } else if (navigate) {
        navigate('/login', { state: { from: window.location.pathname } })
      }
      return
    }
    // Navigate to entity detail page where the user can confirm with a photo
    const targetEntityId = entity?.entityId || entity?.entity_id || entity?.id
    if (navigate && targetEntityId) {
      navigate(`/entity/${targetEntityId}`)
    }
    onDismiss(entityId)
  }, [user, entity, entityId, onDismiss, isImmersiveMode, onTabChange, navigate])

  const handleDismiss = useCallback((e) => {
    e.stopPropagation()
    onDismiss(entityId)
  }, [onDismiss, entityId])

  if (!entity) return null

  const seenAt = entity.lastSeenAt || entity.last_seen_at
  const spottedAgo = seenAt ? formatRelativeTime(seenAt) : null

  return (
    <div className="bg-white border border-gray-200 shadow-md rounded-xl px-4 py-3 flex items-center gap-3 pointer-events-auto w-full">
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 leading-tight">
            {t('nearbyPrompt.looksLike') || 'Looks like you\'re near'}
          </p>
          <p className="text-sm font-semibold text-gray-900 truncate">{entity.name}</p>
          {spottedAgo && (
            <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
              {t('nearbyPrompt.spottedAgo', { time: spottedAgo }) || `Spotted ${spottedAgo}`}
            </p>
          )}
        </div>

        {/* Action button — confirm for others, still here for own sighting */}
        {isOwnSighting ? (
          <button
            onClick={handleStillHere}
            onTouchEnd={(e) => { e.preventDefault(); handleStillHere(e) }}
            disabled={isStillHereLoading}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('sightings.stillHereTitle') || 'Submit a fresh sighting at this location'}
          >
            <MapPinIcon className="w-4 h-4 flex-shrink-0" />
            <span>{isStillHereLoading ? (t('map.spotting') || 'Updating…') : (t('sightings.stillHere') || 'Still Here')}</span>
          </button>
        ) : (
          <button
            onClick={handleConfirmWithPhoto}
            onTouchEnd={(e) => { e.preventDefault(); handleConfirmWithPhoto(e) }}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-primary/50 bg-primary/5 text-gray-700 hover:bg-primary/15 hover:border-primary transition-colors"
          >
            <CameraIcon className="w-4 h-4 flex-shrink-0" />
            <span>
              {user
                ? (t('confirm.withPhoto') || 'Confirm with Photo')
                : (t('nearbyPrompt.loginToConfirm') || 'Log in to Confirm')}
            </span>
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          onTouchEnd={handleDismiss}
          className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          title={t('app.dismiss') || 'Dismiss'}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
    </div>
  )
})

export default NearbyEntityPrompt
